import { IconButton, Tooltip } from '@civilgrid/ui/components';
import { useMemo, useRef, useState } from 'react';
import { useIsMounted } from '~/hooks/useIsMounted';
import Form from '../forms/Form';
import UploadButton from '../forms/UploadButton';
import { Notify } from '../Notify';
import ChatBox from './ChatBox';

// max file upload at once 1 GB
const MAX_SIZE_UPLOAD = 1073741824;

interface Props {
  isProcessing?: boolean;
  onSubmit?: (message: string, files: File[]) => Promise<void>;
}

export default function MessageForm({ isProcessing, onSubmit }: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const isMounted = useIsMounted();

  const isFormEmpty = useMemo<boolean>(() => {
    if (!value && !files.length) return true;
    return false;
  }, [value, files]);

  const onSend = async () => {
    if (!files.length && !value) return;

    await onSubmit?.(value, files);

    if (isMounted()) {
      setValue('');
      setFiles([]);
      ref.current?.focus();
    }
  };

  return (
    <Form
      className="w-full"
      onSubmit={(e) => {
        e.preventDefault();
        onSend();
      }}
    >
      <div className="flex grow items-center space-x-2">
        <Tooltip
          asChild
          text={
            files.length ? (
              <div className="flex flex-col items-start text-xs">
                <div className="font-bold">Selected Files:</div>
                {files.map((f) => (
                  <div key={`${f.name}-${f.size}-${f.lastModified}`}>{f.name}</div>
                ))}
              </div>
            ) : (
              'Attach files'
            )
          }
        >
          <div className="relative">
            {files.length > 0 && <div className="absolute right-0 top-0 h-2 w-2 rounded-full bg-brand" />}

            <UploadButton
              icon="paperclip"
              ghost
              multiple
              onUpload={(selected) => {
                const size = selected.map((f) => f.size).reduce((sum, current) => sum + current, 0);

                if (size > MAX_SIZE_UPLOAD) {
                  Notify.error({ message: 'Selected files are too large' });
                  return;
                }

                setFiles([...selected]);
                ref.current?.focus();
              }}
              className="h-[30px] w-[30px]"
            />
          </div>
        </Tooltip>

        <ChatBox ref={ref} value={value} onChange={setValue} onSubmit={onSend} placeholder="Write a message..." />

        <Tooltip asChild text={'Send message'}>
          <IconButton
            disabled={isProcessing || isFormEmpty}
            loading={isProcessing}
            icon="envelopeClosed"
            size="icon"
            iconSize={16}
            variant="secondary"
            type="submit"
            aria-label="Send message"
            className="p-1.5"
          />
        </Tooltip>
      </div>
    </Form>
  );
}
